import React, { useState } from 'react';
import { Modal, Form, Select, Input, Alert } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

const REASONS = [
    'speeding',
    'straight_lining',
    'pattern_response',
    'failed_attention_check',
    'inconsistent_responses',
    'duplicate_fingerprint',
    'suspicious_device',
    'low_quality_text',
    'other_quality_concern',
];

interface RejectionDialogProps {
    open: boolean;
    submitting: boolean;
    onCancel: () => void;
    onSubmit: (reason: string, notes?: string) => Promise<void> | void;
}

export const RejectionDialog: React.FC<RejectionDialogProps> = ({
    open,
    submitting,
    onCancel,
    onSubmit,
}) => {
    const intl = useIntl();
    const [reason, setReason] = useState<string | undefined>(undefined);
    const [notes, setNotes] = useState<string>('');

    const reset = () => {
        setReason(undefined);
        setNotes('');
    };

    const handleOk = async () => {
        if (!reason) return;
        await onSubmit(reason, notes || undefined);
        reset();
    };

    const handleCancel = () => {
        reset();
        onCancel();
    };

    return (
        <Modal
            title={<FormattedMessage id="responseIntegrity.rejectDialog.title" />}
            open={open}
            onOk={handleOk}
            onCancel={handleCancel}
            okText={intl.formatMessage({ id: 'responseIntegrity.rejectDialog.submit' })}
            okButtonProps={{ danger: true, disabled: !reason, loading: submitting }}
            cancelText={intl.formatMessage({ id: 'common.cancelButton' })}
        >
            <Alert
                type="warning"
                showIcon
                message={<FormattedMessage id="responseIntegrity.rejectDialog.intro" />}
                style={{ marginBottom: 16 }}
            />
            <Form layout="vertical">
                <Form.Item
                    label={<FormattedMessage id="responseIntegrity.rejectDialog.reasonLabel" />}
                    required
                >
                    <Select
                        value={reason}
                        onChange={setReason}
                        placeholder={intl.formatMessage({
                            id: 'responseIntegrity.rejectDialog.reasonPlaceholder',
                        })}
                        options={REASONS.map((code) => ({
                            value: code,
                            label: intl.formatMessage({ id: `responseIntegrity.reasons.${code}` }),
                        }))}
                    />
                </Form.Item>
                <Form.Item
                    label={<FormattedMessage id="responseIntegrity.rejectDialog.notesLabel" />}
                >
                    <Input.TextArea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        placeholder={intl.formatMessage({
                            id: 'responseIntegrity.rejectDialog.notesPlaceholder',
                        })}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};
